import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { examAPI, questionAPI, testAPI } from '../lib/api';
import { Filter, BookOpen, Target, Calendar, TrendingUp, Sparkles, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { renderLatex } from '../lib/renderLatex';
import LoadingSpinner from '../components/LoadingSpinner';

const PYQBrowser = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  
  const [filters, setFilters] = useState({
    exam_id: 'all',
    year: 'all',
    difficulty: 'all',
  });

  useEffect(() => {
    loadExams();
  }, []);

  useEffect(() => {
    if (filters.exam_id && filters.exam_id !== 'all') {
      loadQuestions();
    } else {
      setQuestions([]);
    }
  }, [filters]);

  const loadExams = async () => {
    try {
      const response = await examAPI.getAll();
      setExams(response.data);
    } catch (error) {
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.exam_id && filters.exam_id !== 'all') params.exam_id = filters.exam_id;
      if (filters.year && filters.year !== 'all') params.year = parseInt(filters.year);
      if (filters.difficulty && filters.difficulty !== 'all') params.difficulty = filters.difficulty;
      
      const response = await questionAPI.getAll(params);
      setQuestions(response.data);
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectQuestion = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleCreateTestFromPYQs = async () => {
    if (selectedQuestions.length === 0) {
      toast.error('Please select at least one question');
      return;
    }

    try {
      const response = await testAPI.generate({
        exam_id: filters.exam_id,
        num_questions: selectedQuestions.length,
        test_type: 'pyq'
      });
      toast.success('PYQ test created!');
      navigate(`/test/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create test');
    }
  };

  const handleQuickPractice = (examId) => {
    navigate(`/test/create?exam=${examId}`);
  };

  const years = [2024, 2023, 2022, 2021, 2020, 2019, 2018];
  const difficulties = ['easy', 'medium', 'hard'];
  
  // Get exams with PYQ years
  const examsWithPYQs = exams.filter(e => e.pyq_years && e.pyq_years.length > 0);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-[#0071E3] px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles size={16} />
            <span>Previous Year Questions Bank</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            PYQ Browser
          </h1>
          <p className="text-lg text-gray-500">
            Practice with real previous year questions from actual exams
          </p>
        </div>

        {/* Quick Access - Popular PYQs */}
        <Card className="p-6 mb-8 bg-white rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="text-[#0071E3]" size={20} />
            Quick Practice - Popular Exams
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {examsWithPYQs.slice(0, 6).map((exam) => (
              <button
                key={exam.id}
                onClick={() => handleQuickPractice(exam.id)}
                className="p-4 rounded-xl border border-gray-100 hover:border-[#0071E3] hover:bg-blue-50 transition-all text-center group"
              >
                <div className="text-2xl mb-2">{exam.icon || '📚'}</div>
                <p className="font-semibold text-gray-900 text-sm">{exam.name}</p>
                {exam.pyq_years && (
                  <p className="text-xs text-gray-500 mt-1">
                    {exam.pyq_years.length} years
                  </p>
                )}
                <ChevronRight className="w-4 h-4 mx-auto mt-2 text-gray-400 group-hover:text-[#0071E3]" />
              </button>
            ))}
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-6 mb-8 bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <Filter className="text-[#0071E3]" size={24} />
            <h2 className="text-lg font-bold text-gray-900">Filter Questions</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">Exam</label>
              <Select 
                value={filters.exam_id} 
                onValueChange={(value) => setFilters({ ...filters, exam_id: value })}
              >
                <SelectTrigger className="h-12 rounded-xl bg-gray-50 border-gray-200">
                  <SelectValue placeholder="Select exam" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Exams</SelectItem>
                  {exams.filter(exam => exam.is_available).map((exam) => (
                    <SelectItem key={exam.id} value={exam.id}>
                      {exam.icon} {exam.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="coming-soon" disabled className="text-muted-foreground">
                    ── Coming Soon ──
                  </SelectItem>
                  {exams.filter(exam => !exam.is_available).map((exam) => (
                    <SelectItem key={exam.id} value={exam.id} disabled>
                      🔒 {exam.icon} {exam.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">Year</label>
              <Select 
                value={filters.year} 
                onValueChange={(value) => setFilters({ ...filters, year: value })}
              >
                <SelectTrigger className="h-12 rounded-xl bg-gray-50 border-gray-200">
                  <SelectValue placeholder="All years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">Difficulty</label>
              <Select 
                value={filters.difficulty} 
                onValueChange={(value) => setFilters({ ...filters, difficulty: value })}
              >
                <SelectTrigger className="h-12 rounded-xl bg-gray-50 border-gray-200">
                  <SelectValue placeholder="All levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  {difficulties.map((diff) => (
                    <SelectItem key={diff} value={diff}>
                      {diff.charAt(0).toUpperCase() + diff.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {selectedQuestions.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <p className="text-gray-900 font-semibold">
                  {selectedQuestions.length} question{selectedQuestions.length !== 1 ? 's' : ''} selected
                </p>
                <Button
                  onClick={handleCreateTestFromPYQs}
                  className="rounded-full bg-[#0071E3] hover:bg-blue-600 text-white"
                >
                  <Target className="mr-2" size={18} />
                  Create PYQ Test
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Questions List */}
        {loading ? (
          <LoadingSpinner text="Loading questions..." />
        ) : filters.exam_id === 'all' ? (
          <Card className="p-12 text-center bg-white rounded-2xl shadow-sm border border-gray-100">
            <Search className="text-gray-400 mx-auto mb-4" size={64} />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Select an Exam</h3>
            <p className="text-gray-500 mb-6">Choose an exam from the filter above to browse PYQs</p>
            <div className="flex flex-wrap justify-center gap-2">
              {exams.slice(0, 5).map(exam => (
                <Button
                  key={exam.id}
                  variant="outline"
                  onClick={() => setFilters({ ...filters, exam_id: exam.id })}
                  className="rounded-full"
                >
                  {exam.icon} {exam.name}
                </Button>
              ))}
            </div>
          </Card>
        ) : questions.length === 0 ? (
          <Card className="p-12 text-center bg-white rounded-2xl shadow-sm border border-gray-100">
            <BookOpen className="text-gray-400 mx-auto mb-4" size={64} />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Questions Found</h3>
            <p className="text-gray-500 mb-4">No PYQs available for this filter combination</p>
            <p className="text-sm text-gray-400">
              <Sparkles className="inline mr-1" size={14} />
              AI will generate exam-pattern questions when you start a test!
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                Showing {questions.length} question{questions.length !== 1 ? 's' : ''}
              </p>
              <Button
                variant="outline"
                onClick={() => setSelectedQuestions(questions.map(q => q.id))}
                className="rounded-full text-sm"
              >
                Select All
              </Button>
            </div>
            
            {questions.map((question, index) => (
              <Card key={question.id} className="p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0071E3] rounded-xl flex items-center justify-center text-white font-bold">
                      {index + 1}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {question.year && (
                        <Badge className="bg-blue-50 text-[#0071E3] hover:bg-blue-100">
                          <Calendar size={12} className="mr-1" />
                          {question.year}
                        </Badge>
                      )}
                      {question.tags?.includes('PYQ') && (
                        <Badge className="bg-amber-50 text-amber-700">
                          PYQ
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => toggleSelectQuestion(question.id)}
                    variant={selectedQuestions.includes(question.id) ? "default" : "outline"}
                    size="sm"
                    className="rounded-full"
                  >
                    {selectedQuestions.includes(question.id) ? '✓ Selected' : 'Select'}
                  </Button>
                </div>
                
                {/* Question Text */}
                <p 
                  className="text-gray-900 dark:text-gray-100 font-medium mb-4"
                  dangerouslySetInnerHTML={{ __html: renderLatex(question.question_text) }}
                />
                
                {/* Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {question.options?.map((option) => (
                    <div
                      key={option.label}
                      className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm"
                    >
                      <span className="font-semibold text-[#0071E3] mr-2">{option.label}.</span>
                      <span dangerouslySetInnerHTML={{ __html: renderLatex(option.text) }} />
                    </div>
                  ))}
                </div>
                
                {/* Correct Answer & Explanation (hidden by default) */}
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-[#0071E3] font-medium">
                    View Answer
                  </summary>
                  <div className="mt-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">
                      <strong>Correct Answer:</strong> {question.correct_answer}
                    </p>
                    {question.explanation && (
                      <p 
                        className="text-sm text-gray-600 dark:text-gray-300 mt-2"
                        dangerouslySetInnerHTML={{ __html: renderLatex(question.explanation) }}
                      />
                    )}
                  </div>
                </details>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PYQBrowser;
